import React, {
	useState,
	useEffect,
	useRef,
} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Tab from 'tbk-components/src/components/Tab';
import Tabs from 'tbk-components/src/components/Tabs';
import AnimatedArcs from '../../common/AnimatedArcs';
import Tables, { ITablesProps } from '../Tables';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';
import VideoShowcase from '../VideoShowcase';
import { IVideoModalProps } from '../VideoModal';

export interface ITabContentProps {
	label?: string;
	imagesOrientation?: 'Left' | 'Right';
	imageOrientation: 'Horizontal' | 'Vertical';
	images?: IImage[];
	videoModal?: IVideoModalProps;
	/**
	 * Primary Column
	 * @control richtext
	 */
	leadRow?: string;
	/**
	 * Primary Column
	 * @control richtext
	 */
	primaryColumn?: string;
	/**
	 * Secondary Column
	 * @control richtext
	 */
	secondaryColumn?: string;
	/**
	 * Tertiary Column will not be shown if images are presented.
	 * @control richtext
	 */
	tertiaryColumn?: string;
	/**
	 * @noUI
	 */
	tableContent?: ITablesProps;
	columnListCount?: 'One' | 'Two' | 'Three' | 'Four';
	primaryCTA?: ILink;
	secondaryCTA?: ILink;
	lightboxCTA?: IFormLightboxButtonProps;
	/**
	 * @noUI
	 */
	tabContent: any;
}

export interface ITabbedContentProps extends IBaseElementProps {
	mainBackgroundColor?: string;
	mainBackgroundImage?: IImage;
	mainHeading?: string;
	/**
	 * @control textarea
	 */
	mainDescription?: string;
	mainAlignment?: 'Left' | 'Center';
	mainIcons: IImage[];
	mainCTA?: ILink;
	tabStyle: 'Default' | 'Accordion';
	tabs?: ITabContentProps[];
	tabsOrientation?: 'Vertical' | 'Horizontal';
	enableLineAnimations?: boolean;
	lineAnimationDelay?: number;
	lineAnimationOffset?: number;
	arcsPosition?: 'Right' | 'Left';
}

/**
 * Calian Tabbed Content
 * @icon format-gallery
 * @block
 */
const TabbedContent: React.FC<ITabbedContentProps> = ({
		id,
		className,
		classNames = [],
		mainBackgroundColor,
		mainBackgroundImage,
		mainHeading,
		mainAlignment,
		mainDescription,
		mainIcons,
		mainCTA,
		tabs,
		tabStyle = 'Default',
		tabsOrientation = 'Vertical',
		enableLineAnimations = false,
		arcsPosition = 'Right',
		lineAnimationDelay = 250,
		lineAnimationOffset = 150,
	}) => {
		const [tabButtonLeft, setTabButtonLeft] = useState<boolean>(false);
		const [tabButtonRight, setTabButtonRight] = useState<boolean>(false);
		const ref = useRef<HTMLDivElement>(null);
		const btnRef = useRef<HTMLButtonElement>(null);
		const [activeTab, setActiveTab] = useState(0);

		useEffect(() => {
			if (!ref.current) {
				return;
			}

			const setTabButtons = () => {
				const navTabs = ref.current?.querySelector(
					'.nav-tabs',
				) as HTMLElement;

				if (navTabs.scrollWidth > navTabs.clientWidth) {
					setTabButtonRight(true);
					setTabButtonLeft(false);
				} else {
					setTabButtonRight(false);
					setTabButtonLeft(false);
				}

				const buttons = ref.current?.querySelectorAll(
					'.calian-tabbed-content__tab-button-left button, .calian-tabbed-content__tab-button-right button',
				) as NodeListOf<HTMLElement>;

				buttons.forEach((button) => {
					button.setAttribute('tabIndex', '-1');
				});
			};

			const links = document.querySelectorAll(
				'.calian-tabbed-content__accordion-pane .calian-tabbed-content__content a',
			) as NodeListOf<HTMLAnchorElement>;
			const linksStopProp = (
				eventType: 'addEventListener' | 'removeEventListener',
			) => {
				links.forEach((a) => {
					a[eventType]('click', (e) => {
						e.stopPropagation();
					});
				});
			};
			const moveToHash = () => {
				const urlHash = window.location.hash;

				if (!urlHash) {
					return;
				}

				const target = ref.current?.querySelector(
					urlHash,
				) as HTMLButtonElement;

				if (!target) {
					return;
				}

				target.click();
			};

			linksStopProp('addEventListener');

			window.addEventListener('load', () => {
				setTabButtons();
				moveToHash();
			});
			window.addEventListener('resize', setTabButtons);

			return () => {
				window.removeEventListener('load', () => {
					setTabButtons();
					moveToHash();
				});
				window.removeEventListener('resize', setTabButtons);

				linksStopProp('removeEventListener');
			};
		}, []);

		useEffect(() => {
			const assignHeight = () => {
				const tabs = document.querySelectorAll(
					'.calian-tabbed-content__tabs.calian-tabbed-content__tabs--vertical .tab-pane',
				) as NodeListOf<HTMLElement>;
				const calcHeight = () => {
					const h = [];

					tabs.forEach((tab) => {
						tab.style.display = 'block';
						h.push(tab.offsetHeight);
						tab.style.display = '';
					});

					return Math.max(...h);
				};

				tabs.forEach((tab) => {
					if (window.innerWidth >= 992) {
						tab.style.height = `${calcHeight()}px`;
					} else {
						tab.style.height = '';
					}
				});
			};

			const navTabs = ref.current?.querySelector('.nav-tabs') as HTMLElement;

			navTabs.addEventListener('scroll', () => {
				if (!btnRef.current.classList.contains('touched')) {
					btnRef.current.classList.add('touched');
				}
			});

			window.addEventListener('load', assignHeight);
			window.addEventListener('resize', assignHeight);

			return () => {
				window.removeEventListener('load', assignHeight);
				window.removeEventListener('resize', assignHeight);
				navTabs.removeEventListener('scroll', () => {
					if (!btnRef.current.classList.contains('touched')) {
						btnRef.current.classList.add('touched');
					}
				});
			};
		}, []);

		const scrollTabRight = () => {
			const navTabs = ref.current?.querySelector('.nav-tabs') as HTMLElement;

			const containerWidth = navTabs.clientWidth;
			const offset = navTabs.scrollWidth - navTabs.clientWidth;
			const scollLeft = navTabs.scrollLeft;
			const end = containerWidth + scollLeft;

			const tabs = ref.current?.querySelectorAll(
				'.nav-tabs li',
			) as NodeListOf<HTMLElement>;

			let width = 0;
			let next = true;
			let scroll = 0;

			tabs.forEach((tab) => {
				if (next === true) {
					width += tab.offsetWidth;
					if (width > end) {
						scroll = width - containerWidth;
						next = false;
					}
				}
			});

			navTabs.scrollLeft = scroll;
			if (navTabs.scrollLeft >= offset - 1) {
				setTabButtonRight(false);
			}
			setTabButtonLeft(true);
		};

		const scrollTabLeft = () => {
			const navTabs = ref.current?.querySelector('.nav-tabs') as HTMLElement;

			const containerWidth = navTabs.clientWidth;
			const scollLeft = navTabs.scrollLeft;
			const end = containerWidth + scollLeft;

			const tabs = ref.current?.querySelectorAll(
				'.nav-tabs li',
			) as NodeListOf<HTMLElement>;

			let width = 0;
			let next = true;
			let scroll = 0;

			tabs.forEach((tab, index) => {
				if (next === true) {
					width += tab.offsetWidth;
					if (width >= end) {
						scroll = width - tab.offsetWidth - containerWidth;
						next = false;
					}
				}
			});
			navTabs.scrollLeft = scroll;

			if (navTabs.scrollLeft === 0) {
				setTabButtonLeft(false);
			}

			setTabButtonRight(true);
		};

		return (
			<div
				id={id}
				className={
					className ||
					cn(
						'calian-tabbed-content position-relative',
						tabStyle !== 'Accordion' && 'pb-7 pb-lg-12',
						mainBackgroundColor &&
							!(
								mainBackgroundColor == 'white' ||
								mainBackgroundColor == '#fff' ||
								mainBackgroundColor == '#fffff'
							) &&
							'py-7 pt-lg-11',
						...classNames,
					)
				}
				style={{
					backgroundColor: mainBackgroundColor ? mainBackgroundColor : '',
				}}
				ref={ref}
			>
				{mainBackgroundImage?.src && (
					<div
						className="position-absolute top-0 bottom-0 end-0 d-none d-md-block h-100 w-100"
						style={{
							backgroundImage: `url(${mainBackgroundImage?.src})`,
							backgroundSize: 'cover',
							backgroundPosition: 'top right',
						}}
					></div>
				)}
				{enableLineAnimations && (
					<AnimatedArcs
						arcsStyle={'Component - Two Lines'}
						arcsPosition={arcsPosition}
						arcOneColor="var(--bs-primary-mid)"
						arcOneStrokeWidth={4}
						arcTwoColor="var(--bs-primary)"
						arcTwoStrokeWidth={3}
						animationDelay={lineAnimationDelay}
						animationOffset={lineAnimationOffset}
					/>
				)}
				<div className="container-fluid mw-1260px z-1 position-relative">
					<div className="row mb-md-4 ms-0 ms-xl-3">
						<div
							className={cn(
								'col-12 col-lg-7',
								mainAlignment === 'Center' && 'mx-auto text-center',
							)}
						>
							<div className="d-flex flex-column gap-3">
								{mainHeading && (
									<h2 className="mb-0">{mainHeading}</h2>
								)}
								{mainDescription && (
									<p className="body-lg mb-0">
										{mainDescription}
									</p>
								)}
							</div>
							{mainIcons && mainIcons.length > 0 && (
								<div className="d-flex flex-row justify-content-start align-items-center mt-4">
									{mainIcons.map((image, index) => (
										<Image
											key={index}
											{...image}
											className="img-fluid w-28px h-28px object-fit-cover rounded-circle me-4"
										/>
									))}
								</div>
							)}
							{(mainCTA?.label || mainCTA?.href) && (
								<Button
									className="btn btn-primary mt-4"
									href={mainCTA.href}
									target={mainCTA.target}
								>
									{mainCTA.label}
								</Button>
							)}
						</div>
					</div>
					{mainHeading && <div className="mt-5 mt-md-9"></div>}
					{tabs && tabs.length > 0 && (
						<div className="row position-relative">
							{tabStyle === 'Accordion' ? (
								<>
									{tabs && tabs.length > 0 && (
										<>
											<Tabs className="calian-tabbed-content__tabs d-flex z-2 calian-tabbed-content__tabs--vertical flex-column d-md-none">
												{tabs &&
													tabs.length > 0 &&
													tabs.map((tab, index) => (
														<Tab
															title={tab.label}
															key={index}
														>
															<div className="calian-tabbed-content__pane d-flex flex-row mt-5">
																{tab?.images
																	?.length >
																	0 && (
																	<div className="calian-tabbed-content__images d-flex gap-3 gap-md-5 flex-row justify-content-center">
																		{tab.images.map(
																			(
																				img,
																				index,
																			) => (
																				<div
																					key={
																						index
																					}
																				>
																					<Image
																						{...img}
																						key={
																							index
																						}
																						className="img-fluid rounded-5"
																					/>
																				</div>
																			),
																		)}
																	</div>
																)}
																{tab.primaryColumn && (
																	<div className="calian-tabbed-content__content order-2">
																		{tab.primaryColumn && (
																			<div
																				dangerouslySetInnerHTML={{
																					__html: tab.primaryColumn,
																				}}
																			/>
																		)}
																		{tabsOrientation ===
																			'Vertical' &&
																			tab
																				.primaryCTA
																				?.label
																				.length >
																				0 && (
																				<div className="d-inline-flex gap-1 order-4 mt-5 mt-md-9">
																					<Button
																						className="btn btn-sm btn-primary"
																						href={
																							tab
																								.primaryCTA
																								.href
																						}
																						target={
																							tab
																								.primaryCTA
																								.target
																						}
																					>
																						{
																							tab
																								.primaryCTA
																								.label
																						}
																					</Button>
																					{tab
																						.secondaryCTA
																						?.label
																						.length >
																						0 && (
																						<Button
																							className="btn btn-sm btn-secondary"
																							href={
																								tab
																									.secondaryCTA
																									.href
																							}
																							target={
																								tab
																									.secondaryCTA
																									.target
																							}
																						>
																							{
																								tab
																									.secondaryCTA
																									.label
																							}
																						</Button>
																					)}
																					{tab
																						.lightboxCTA
																						?.formLightboxButton
																						?.label
																						?.length >
																						0 && (
																						<FormLightboxButton
																							{...tab.lightboxCTA}
																						/>
																					)}
																				</div>
																			)}
																		{
																			tab.secondaryCTA
																		}
																	</div>
																)}
																{tab.tabContent}
															</div>
														</Tab>
													))}
											</Tabs>
											<div className="calian-tabbed-content__accordion d-none d-md-flex p-0">
												{tabs.map((tab, index) => {
													const tabPane = (
														<div className="calian-tabbed-content__accordion-pane text-start">
															<div className="calian-tabbed-content__accordion-label text-white">
																{tab.label}
															</div>
															{tab?.images?.length >
																0 && (
																<div className=" calian-tabbed-content__accordion-images d-flex gap-6 mt-9 ms-5">
																	{tab.images.map(
																		(
																			img,
																			index,
																		) => (
																			<div
																				key={
																					index
																				}
																			>
																				<Image
																					{...img}
																				/>
																			</div>
																		),
																	)}
																</div>
															)}
															{tab.primaryColumn && (
																<div
																	className="calian-tabbed-content__content mt-6 mx-5"
																	dangerouslySetInnerHTML={{
																		__html: tab.primaryColumn,
																	}}
																/>
															)}
															{tab.tabContent}
														</div>
													);

													return (
														<div
															key={index}
															className={cn(
																`calian-tabbed-content__accordion-pane-wrapper px-5 pt-4 pb-7`,
																tabStyle ===
																	'Accordion' &&
																	activeTab ===
																		index &&
																	'active',
															)}
														>
															{tabStyle ===
															'Accordion' ? (
																<div
																	onClick={() => {
																		setActiveTab(
																			index,
																		);
																	}}
																	onKeyDown={(
																		e,
																	) => {
																		if (
																			e.key ===
																			'Enter'
																		) {
																			setActiveTab(
																				index,
																			);
																		}
																	}}
																	tabIndex={0}
																>
																	{tabPane}
																</div>
															) : (
																tabPane
															)}
														</div>
													);
												})}
											</div>
										</>
									)}
								</>
							) : (
								<>
									{tabButtonLeft && (
										<div
											className={cn(
												'calian-tabbed-content__tab-button-left',
												mainBackgroundColor &&
													'calian-tabbed-content__tab-button-left--background-colour',
											)}
											style={{
												backgroundColor: mainBackgroundColor
												? mainBackgroundColor
												: '',
											}}
										>
											<button
												className="d-none d-md-block"
												aria-label="Slide Tab Left"
												onClick={() => {
													scrollTabLeft();
												}}
											></button>
										</div>
									)}

								{tabButtonRight && (
									<div
										className={cn(
											'calian-tabbed-content__tab-button-right',
											mainBackgroundColor &&
												'calian-tabbed-content__tab-button-right--background-colour',
										)}
										style={{
											backgroundColor: mainBackgroundColor
												? mainBackgroundColor
												: '',
										}}
									>
										<button
											ref={btnRef}
											aria-label="Slide Tab Right"
											onClick={() => {
												scrollTabRight();
											}}
										></button>
									</div>
								)}
								<Tabs
									className={cn(
										'calian-tabbed-content__tabs d-flex z-2',
										tabsOrientation === 'Horizontal' &&
											'calian-tabbed-content__tabs--horizontal flex-row',
										tabsOrientation === 'Vertical' &&
											'calian-tabbed-content__tabs--vertical flex-column',
									)}
								>
									{tabs &&
										tabs.length > 0 &&
										tabs.map((tab, index) => (
											<Tab title={tab.label} key={index}>
												{(tab.leadRow ||
													tab.tableContent) && (
													<div className="calian-tabbed-content__pane-lead-row mt-5 mt-md-7 mt-xl-9">
														{tab.leadRow && (
															<div
																dangerouslySetInnerHTML={{
																	__html: tab.leadRow,
																}}
															/>
														)}
														{tab.tableContent && (
															<div>
																<Tables
																	{...tab.tableContent}
																/>
															</div>
														)}
													</div>
												)}
												<div
													className={cn(
														'calian-tabbed-content__pane d-flex',
														tab.images &&
															tab.images.length >
																0 &&
															tab.primaryColumn &&
															tab.secondaryColumn &&
															'calian-tabbed-content__pane--3-col',
														tab.images &&
															tab.images.length >
																0 &&
															tab.primaryColumn &&
															!tab.secondaryColumn &&
															'calian-tabbed-content__pane--2-col',
														tabsOrientation ===
															'Horizontal' &&
															'flex-column gap-5 gap-md-6 mt-5 mt-md-0',
														tabsOrientation ===
															'Vertical' &&
															'flex-row gap-lg-7',
														tabsOrientation ===
															'Vertical' &&
															enableLineAnimations &&
															'mt-5 mt-md-7 mt-0 mb--md-12',
														tabsOrientation ===
															'Vertical' &&
															!enableLineAnimations &&
															'mt-5 mt-md-7 mt-0 mb--md-9',
														tab.leadRow &&
															'mt-xl-9',
													)}
												>
													{tab.images &&
														tab.images.length >
															0 && (
															<div
																className={cn(
																	'calian-tabbed-content__images d-flex gap-3 gap-md-5',
																	tab.imagesOrientation ===
																		'Left' &&
																		tab.imageOrientation ===
																			'Horizontal' &&
																		'order-1 md-order-1 justify-content-start align-items-start',
																	tab.imagesOrientation ===
																		'Left' &&
																		tab.imageOrientation ===
																			'Vertical' &&
																		'order-1 md-order-1 justify-content-start align-items-end',
																	tab.imagesOrientation ===
																		'Right' &&
																		tab.imageOrientation ===
																			'Horizontal' &&
																		'order-1 order-lg-4 justify-content-end align-items-start',
																	tab.imagesOrientation ===
																		'Right' &&
																		'Left' &&
																		tab.imageOrientation ===
																			'Vertical' &&
																		'order-1 order-lg-4 justify-content-start align-items-end',
																	tab.imageOrientation ===
																		'Horizontal' &&
																		'flex-row',
																	tab.imageOrientation ===
																		'Vertical' &&
																		'flex-column justify-content-start',
																)}
															>
																{tab.images.map(
																	(
																		img,
																		index,
																	) => (
																		<div
																			key={
																				index
																			}
																		>
																			<Image
																				{...img}
																				className="img-fluid rounded-5"
																			/>
																		</div>
																	),
																)}
															</div>
														)}
													{(tab?.videoModal?.url
														?.src ||
														tab?.videoModal
															?.oembed) && (
														<div
															className={cn(
																'calian-tabbed-content__video',
																tab.imagesOrientation ===
																	'Left' &&
																	tab.imageOrientation ===
																		'Horizontal' &&
																	'order-1 justify-content-start align-items-start',
																tab.imagesOrientation ===
																	'Left' &&
																	tab.imageOrientation ===
																		'Vertical' &&
																	'order-1 justify-content-start align-items-end',
																tab.imagesOrientation ===
																	'Right' &&
																	tab.imageOrientation ===
																		'Horizontal' &&
																	'order-1 order-lg-4 justify-content-end align-items-start',
																tab.imagesOrientation ===
																	'Right' &&
																	'Left' &&
																	tab.imageOrientation ===
																		'Vertical' &&
																	'order-1 order-lg-4 justify-content-start align-items-end',
																tab.imageOrientation ===
																	'Horizontal' &&
																	'flex-row',
																tab.imageOrientation ===
																	'Vertical' &&
																	'flex-column justify-content-start',
															)}
														>
															<VideoShowcase
																style="Horizontal"
																video={
																	tab.videoModal
																}
																videoStyle="modal"
															/>
														</div>
													)}
													{tab.primaryColumn && (
														<div
															className={cn(
																'calian-tabbed-content__content order-2 mb-6 mb-lg-0',
																`calian-tabbed-content__content--${
																	tab.columnListCount
																		? tab.columnListCount.toLowerCase()
																		: 'one'
																}-column-list`,
															)}
														>
															{tab.primaryColumn && (
																<div
																	dangerouslySetInnerHTML={{
																		__html: tab.primaryColumn,
																	}}
																/>
															)}
															{tabsOrientation ===
																'Vertical' &&
																(tab.primaryCTA ||
																	tab.secondaryCTA ||
																	tab.lightboxCTA) && (
																	<div className="d-inline-flex gap-1 order-4 mt-5 mt-md-6">
																		{tab
																			.primaryCTA
																			?.label
																			.length >
																			0 && (
																			<Button
																				className="btn btn-sm btn-primary"
																				{...tab.primaryCTA}
																			/>
																		)}
																		{tab
																			.secondaryCTA
																			?.label
																			.length >
																			0 && (
																			<Button
																				className="btn btn-sm btn-secondary"
																				{...tab.secondaryCTA}
																			/>
																		)}
																		{tab
																			.lightboxCTA
																			?.formLightboxButton
																			?.label
																			?.length >
																			0 && (
																			<FormLightboxButton
																				{...tab.lightboxCTA}
																			/>
																		)}
																	</div>
																)}
															{tab.tabContent}
														</div>
													)}
													{tab.secondaryColumn &&
														tab.secondaryColumn !==
															'<br>' && (
															<div className="calian-tabbed-content__content order-3">
																<div
																	className=""
																	dangerouslySetInnerHTML={{
																		__html: tab.secondaryColumn,
																	}}
																/>
															</div>
														)}
													{tab?.images?.length <= 0 &&
														tab.tertiaryColumn &&
														tab.tertiaryColumn !==
															'<br>' && (
															<div className="calian-tabbed-content__content order-4">
																<div
																	className=""
																	dangerouslySetInnerHTML={{
																		__html: tab.tertiaryColumn,
																	}}
																/>
															</div>
														)}
													{tabsOrientation ===
														'Horizontal' &&
														tab.primaryCTA?.label
															.length > 0 && (
																<div
																	className={cn(
																		'd-inline-flex gap-1 order-4 mt-0 mb-0 mb-md-0 mb-5'
																	)}
																>
																	<Button
																		className="btn btn-sm btn-primary"
																		{...tab.primaryCTA}
																	/>
																	{tab
																		.secondaryCTA
																		?.label
																		.length >
																		0 && (
																		<Button
																			className="btn btn-sm btn-dark"
																			{...tab.secondaryCTA}
																		/>
																	)}
																	{tab.lightboxCTA
																		?.formLightboxButton
																		?.label
																		?.length >
																		0 && (
																		<FormLightboxButton
																			{...tab.lightboxCTA}
																		/>
																	)}
																</div>
															)}
													</div>
												</Tab>
											))}
									</Tabs>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		);
	}
;
export default TabbedContent;
